/**
 * 지오메트리 계산 관련 기능을 담당하는 클래스
 * 역할: 순수한 지오메트리 계산만 담당 (검증, 변환 제외)
 */
export class GeometryCalculator {
  /**
   * 좌표 배열에서 폴리곤 중심점 계산
   * @param {Array} coordinates - 좌표 배열 [[lng, lat], [lng, lat], ...]
   * @returns {Array} 중심점 [lng, lat]
   */
  calculatePolygonCenterFromCoordinates(coordinates) {
    if (!coordinates || coordinates.length < 3) {
      return [0, 0];
    }

    const minLng = Math.min(...coordinates.map(coord => coord[0]));
    const maxLng = Math.max(...coordinates.map(coord => coord[0]));
    const minLat = Math.min(...coordinates.map(coord => coord[1]));
    const maxLat = Math.max(...coordinates.map(coord => coord[1]));

    return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  }

  /**
   * 폴리곤 지오메트리에서 중심점과 최소 반지름 계산
   * @param {ol/geom/Geometry} geometry - 폴리곤 지오메트리
   * @returns {Object} {center: [x, y], minRadius: number}
   */
  calculateCenterAndMinRadiusFromGeometry(geometry) {
    let coordinates, minRadius;
    const type = geometry.getType();

    if (type === 'Polygon') {
      coordinates = geometry.getCoordinates()[0];
    } else if (type === 'MultiPolygon') {
      coordinates = geometry.getCoordinates()[0][0];
    }

    // 중심점 계산 (extent의 중심)
    const extent = geometry.getExtent();
    const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

    // 최소 반지름 계산 (중심점에서 가장 가까운 꼭지점까지의 거리)
    minRadius = Infinity;
    coordinates.forEach((coord) => {
      const dx = coord[0] - center[0];
      const dy = coord[1] - center[1];
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < minRadius) {
        minRadius = distance;
      }
    });

    return {center, minRadius};
  }

  /**
   * 폴리곤 지오메트리에서 범위(extent) 계산
   * @param {ol/geom/Geometry} geometry - 폴리곤 지오메트리
   * @returns {Array} extent [minX, minY, maxX, maxY]
   */
  calculatePolygonExtentFromGeometry(geometry) {
    return geometry.getExtent();
  }

  /**
   * 원본 폴리곤 대비 현재 폴리곤의 스케일 계산
   * @param {ol/geom/Geometry} originalGeometry - 원본 폴리곤 지오메트리
   * @param {ol/geom/Geometry} currentGeometry - 현재 폴리곤 지오메트리
   * @returns {number} 스케일 비율
   */
  calculatePolygonScaleFromOriginalGeometry(originalGeometry, currentGeometry) {
    if (!originalGeometry) return 1;

    const originalCenter = this.calculateCenterAndMinRadiusFromGeometry(originalGeometry).center;
    const currentCenter = this.calculateCenterAndMinRadiusFromGeometry(currentGeometry).center;

    // 원본 폴리곤의 중심점에서 첫 번째 꼭지점까지의 거리
    const originalCoords = originalGeometry.getCoordinates()[0];
    const originalDistance = this.calculateDistanceBetweenPoints(originalCoords[0], originalCenter);

    // 현재 폴리곤의 중심점에서 첫 번째 꼭지점까지의 거리
    const currentCoords = currentGeometry.getCoordinates()[0];
    const currentDistance = this.calculateDistanceBetweenPoints(currentCoords[0], currentCenter);

    return currentDistance / originalDistance;
  }

  /**
   * 폴리곤 좌표로부터 회전각도 계산
   * @param {Array} coordinates - 좌표 배열 [[lng, lat], [lng, lat], ...]
   * @returns {number} 회전각도 (라디안)
   */
  calculatePolygonRotationFromCoordinates(coordinates) {
    if (!coordinates || coordinates.length < 2) {
      return 0;
    }

    const usedCoordinates = coordinates.slice(0, 4); // 첫 4개 점만 사용

    // 평행사변형 기준: 첫 번째 변과 두 번째 변만 고려
    const firstVector = [
      usedCoordinates[1][0] - usedCoordinates[0][0],
      usedCoordinates[1][1] - usedCoordinates[0][1]
    ];

    const secondVector = [
      usedCoordinates[2][0] - usedCoordinates[1][0],
      usedCoordinates[2][1] - usedCoordinates[1][1]
    ];

    // 첫 번째 벡터의 각도 계산
    const angle = Math.atan2(firstVector[1], firstVector[0]);

    return angle;
  }

  /**
   * 두 지점 간의 거리 계산
   * @param {Array} point1 - 첫 번째 지점 [x, y]
   * @param {Array} point2 - 두 번째 지점 [x, y]
   * @returns {number} 거리
   */
  calculateDistanceBetweenPoints(point1, point2) {
    const dx = point2[0] - point1[0];
    const dy = point2[1] - point1[1];
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * 폴리곤 좌표 유효성 검증 (검증 로직은 별도 클래스로 분리 예정)
   * @param {Array} coordinates - 좌표 배열
   * @returns {boolean} 유효성 여부
   */
  validatePolygonCoordinates(coordinates) {
    if (!coordinates || !Array.isArray(coordinates)) {
      return false;
    }

    if (coordinates.length < 3) {
      return false;
    }

    // 각 좌표가 유효한 형식인지 확인
    return coordinates.every(coord =>
      Array.isArray(coord) &&
      coord.length === 2 &&
      typeof coord[0] === 'number' &&
      typeof coord[1] === 'number'
    );
  }
}

/**
 * 지오메트리 검증 전용 클래스 (역할 분리를 위한 준비)
 * TODO: 향후 별도 파일로 분리 예정
 */
export class GeometryValidator {
  /**
   * 폴리곤 좌표 유효성 검증
   * @param {Array} coordinates - 좌표 배열
   * @returns {Object} {isValid: boolean, errors: Array}
   */
  validatePolygonCoordinates(coordinates) {
    const errors = [];

    if (!coordinates || !Array.isArray(coordinates)) {
      errors.push('좌표 배열이 유효하지 않습니다.');
      return { isValid: false, errors };
    }

    if (coordinates.length < 3) {
      errors.push('폴리곤은 최소 3개의 점이 필요합니다.');
      return { isValid: false, errors };
    }

    // 각 좌표 검증
    coordinates.forEach((coord, index) => {
      if (!Array.isArray(coord) || coord.length !== 2) {
        errors.push(`좌표 ${index}: 유효하지 않은 형식`);
      } else if (typeof coord[0] !== 'number' || typeof coord[1] !== 'number') {
        errors.push(`좌표 ${index}: 숫자 값이 아닙니다`);
      }
    });

    return { isValid: errors.length === 0, errors };
  }

  /**
   * 지오메트리 객체 유효성 검증
   * @param {ol/geom/Geometry} geometry - 지오메트리 객체
   * @returns {Object} {isValid: boolean, errors: Array}
   */
  validateGeometry(geometry) {
    const errors = [];

    if (!geometry) {
      errors.push('지오메트리 객체가 없습니다.');
      return { isValid: false, errors };
    }

    if (!geometry.getType || typeof geometry.getType !== 'function') {
      errors.push('유효하지 않은 지오메트리 객체입니다.');
      return { isValid: false, errors };
    }

    const type = geometry.getType();
    if (!['Polygon', 'MultiPolygon'].includes(type)) {
      errors.push(`지원하지 않는 지오메트리 타입: ${type}`);
      return { isValid: false, errors };
    }

    return { isValid: true, errors: [] };
  }
}
